import { type IBasketProduct } from '@mahawi/eshop-common/dist/src/types';
import { createSlice } from '@reduxjs/toolkit';

import {
  type AddProduct,
  type AddProductResponse,
  EViesValidationStatus,
  type IBasketState,
  type InitialStateResponse,
  type RemoveProduct,
  type RemoveProductResponse,
  type SetDelivery,
  type SetDestinationCountryTypeIsoCode,
  type SetFields,
  type ValidateDiscountCodes,
  type ValidateDiscountCodesResponse,
  type ValidateVies,
  type ValidateViesResponse,
} from './types';

const initialState: IBasketState = {
  inProgress: false,
  products: [],
  delivery: {
    fee: {
      withTax: 0,
      withoutTax: 0,
    },
  },
  packagesCount: 0,
  billingAddress: {
    name: '',
    surname: '',
    email: '',
    phone: '',
    street: '',
    streetNumber: '',
    city: '',
    zip: '',
    countryTypeIsoCode: '',
    companyName: '',
    companyId: '',
    companyTaxId: '',
  },
  deliveryAddress: {
    name: '',
    surname: '',
    email: '',
    phone: '',
    street: '',
    streetNumber: '',
    city: '',
    zip: '',
    countryTypeIsoCode: '',
    companyName: '',
    companyId: '',
    companyTaxId: '',
  },
  deliveryAddressIsDifferent: false,
  termsAndConditions: false,
  heurekaCZShopCertification: false,
  marketingConsent: false,
  discounts: [],
  basketPrices: [],
  isForCompany: false,
  deliveryNote: '',
  viesValidation: { status: EViesValidationStatus.Idle },
};

const filterOutProduct = (
  products: IBasketProduct[],
  productUUID: string,
): IBasketProduct[] =>
  products.filter(({ uuid }: IBasketProduct): boolean => uuid !== productUUID);

const basketSlice = createSlice({
  name: 'Basket',
  initialState,
  reducers: {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    initState: (_state, _action): void => {},

    initStateResponse: (state, action: InitialStateResponse): void => {
      state.products = action.payload.products;
      state.basketPrices = action.payload.basketPrices;
      state.inProgress = false;

      state.products.sort((a, b) => a.uuid.localeCompare(b.uuid));
    },

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    addProduct: (state, _action: AddProduct): void => {
      state.inProgress = true;
      state.delivery.fee.withTax = 0;
      state.delivery.fee.withoutTax = 0;
      state.packagesCount = 0;
    },

    addProductResponse: (state, action: AddProductResponse): void => {
      state.products = filterOutProduct(
        state.products,
        action.payload.product.uuid,
      );

      if (action.payload.product.count > 0) {
        state.products.push(action.payload.product);
      }

      state.inProgress = false;
      state.basketPrices = action.payload.basketPrices;

      state.products.sort((a, b) => a.uuid.localeCompare(b.uuid));
    },

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    removeProduct: (state, _action: RemoveProduct): void => {
      state.inProgress = true;
      state.delivery.fee.withTax = 0;
      state.delivery.fee.withoutTax = 0;
      state.packagesCount = 0;
    },

    removeProductResponse: (state, action: RemoveProductResponse): void => {
      state.products = filterOutProduct(
        state.products,
        action.payload.product.uuid,
      );
      state.inProgress = false;
      state.basketPrices = action.payload.basketPrices;
      state.delivery.fee.withTax = 0;
      state.delivery.fee.withoutTax = 0;
      state.packagesCount = 0;

      state.products.sort((a, b) => a.uuid.localeCompare(b.uuid));
    },

    setDelivery: (state, action: SetDelivery): void => {
      state.delivery.fee.withTax = action.payload.delivery.fee.withTax;
      state.delivery.fee.withoutTax = action.payload.delivery.fee.withoutTax;
      state.packagesCount = action.payload.packagesCount;
    },

    validateVies: (state, action: ValidateVies): void => {
      state.viesValidation = {
        status: EViesValidationStatus.Pending,
        vatNumber: action.payload.vatNumber,
      };
    },

    validateViesResponse: (state, action: ValidateViesResponse): void => {
      const { isValid, vatNumber, name, address } = action.payload;
      if (state.viesValidation.vatNumber !== vatNumber) {
        return;
      }
      state.viesValidation = {
        status:
          isValid === true
            ? EViesValidationStatus.Valid
            : isValid === false
              ? EViesValidationStatus.Invalid
              : EViesValidationStatus.Error,
        vatNumber,
        name,
        address,
      };
    },

    resetViesValidation: (state): void => {
      state.viesValidation = { status: EViesValidationStatus.Idle };
    },

    setFields: (state, action: SetFields): void => {
      state.billingAddress = action.payload.billingAddress;
      state.deliveryAddress = action.payload.deliveryAddress;
      state.termsAndConditions = action.payload.termsAndConditions;
      state.heurekaCZShopCertification =
        action.payload.heurekaCZShopCertification;
      state.marketingConsent = action.payload.marketingConsent;
      state.deliveryAddressIsDifferent =
        action.payload.deliveryAddressIsDifferent;
      state.isForCompany = action.payload.isForCompany;
      state.deliveryNote = action.payload.deliveryNote.trim();
    },
    validateDiscountCodes: (state, action: ValidateDiscountCodes): void => {
      state.inProgress = true;
      state.discounts = action.payload.discounts.map((discount) => ({
        discountType: { code: '' },
        code: discount.code,
        value: 0,
      }));
    },
    validateDiscountCodesResponse: (
      state,
      action: ValidateDiscountCodesResponse,
    ): void => {
      state.inProgress = false;
      state.discounts = action.payload.discounts;
      state.basketPrices = action.payload.basketPrices;
    },
    setDestinationCountryTypeIsoCode: (
      state,
      action: SetDestinationCountryTypeIsoCode,
    ): void => {
      state.deliveryAddress.countryTypeIsoCode =
        action.payload.destinationCountryTypeIsoCode;
      state.billingAddress.countryTypeIsoCode =
        action.payload.destinationCountryTypeIsoCode;
    },
  },
});

export const {
  initStateResponse,
  addProduct,
  initState,
  addProductResponse,
  removeProduct,
  removeProductResponse,
  setDelivery,
  setFields,
  validateDiscountCodes,
  validateDiscountCodesResponse,
  setDestinationCountryTypeIsoCode,
  validateVies,
  validateViesResponse,
  resetViesValidation,
} = basketSlice.actions;

export default basketSlice.reducer;
